import { Injectable } from '@angular/core';
import { clearCacheForPattern, DASHBOARD_SUMMARY_PATTERN } from './interceptors/cache.interceptor';

/**
 * Owns read/clear access to the transient `sessionId` stored in sessionStorage.
 * Written by DashboardComponent (or session-switch UI) when the active academic
 * session changes. Read by authInterceptorFn to attach ?oldSessionId= on every
 * non-session API request.
 *
 * Lives in core/ so the interceptor never needs to import from dashboard/.
 */
@Injectable({ providedIn: 'root' })
export class SessionContextService {
  private readonly SESSION_KEY = 'sessionId';
  private readonly SESSION_NAME_KEY = 'sessionName';
  private readonly WALLET_RELOAD_KEY = 'walletReloaded';
  private readonly SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';
  private readonly SESSION_IS_CURRENT_KEY = 'sessionIsCurrent';
  private readonly DASHBOARD_SUMMARY_SNAPSHOT_KEY = 'dashboardSummarySnapshot';
  private readonly LOGIN_INTRO_SEEN_KEY = 'loginIntroSeen';

  getSessionId(): string | null {
    const id = sessionStorage.getItem(this.SESSION_KEY);
    return id && id.trim() ? id : null;
  }

  setSessionId(id: string): void {
    const prev = sessionStorage.getItem(this.SESSION_KEY);
    sessionStorage.setItem(this.SESSION_KEY, id);
    // Switching to a different session must drop the cached dashboard summary so
    // the next load can't show the previous session's counts/attendance.
    if (prev && prev !== id) {
      clearCacheForPattern(DASHBOARD_SUMMARY_PATTERN);
    }
  }

  clearSessionId(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Session-keyed snapshot of the last dashboard summary, so the dashboard can
   * paint instantly on refresh / back-navigation (no splash to prefetch during)
   * and then reconcile with a fresh fetch. Lives here because direct
   * sessionStorage access is centralized in core/. Payload is opaque (the
   * dashboard layer owns its shape).
   */
  setDashboardSummarySnapshot(summary: unknown): void {
    try {
      sessionStorage.setItem(
        this.DASHBOARD_SUMMARY_SNAPSHOT_KEY,
        JSON.stringify({ sid: this.getSessionId() ?? '', summary }),
      );
    } catch {
      /* storage full / unavailable — snapshot is best-effort */
    }
  }

  /** Returns the cached summary only when it belongs to the current session. */
  getDashboardSummarySnapshot<T>(): T | null {
    try {
      const raw = sessionStorage.getItem(this.DASHBOARD_SUMMARY_SNAPSHOT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { sid: string; summary: T };
      return parsed.sid === (this.getSessionId() ?? '') ? parsed.summary : null;
    } catch {
      return null;
    }
  }

  getSessionName(): string {
    return sessionStorage.getItem(this.SESSION_NAME_KEY) ?? '';
  }

  setSessionName(name: string): void {
    sessionStorage.setItem(this.SESSION_NAME_KEY, name);
  }

  clearSessionName(): void {
    sessionStorage.removeItem(this.SESSION_NAME_KEY);
  }

  getWalletReloaded(): boolean {
    return !!sessionStorage.getItem(this.WALLET_RELOAD_KEY);
  }

  setWalletReloaded(): void {
    sessionStorage.setItem(this.WALLET_RELOAD_KEY, '1');
  }

  clearWalletReloaded(): void {
    sessionStorage.removeItem(this.WALLET_RELOAD_KEY);
  }

  getSidebarCollapsed(): boolean {
    return sessionStorage.getItem(this.SIDEBAR_COLLAPSED_KEY) === '1';
  }

  setSidebarCollapsed(collapsed: boolean): void {
    sessionStorage.setItem(this.SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  }

  // The login page's entrance cascade plays once per browser session.
  getLoginIntroSeen(): boolean {
    return sessionStorage.getItem(this.LOGIN_INTRO_SEEN_KEY) === '1';
  }

  markLoginIntroSeen(): void {
    sessionStorage.setItem(this.LOGIN_INTRO_SEEN_KEY, '1');
  }

  // True when the session in storage is the school's active/current session.
  // Set to false when browsing a past/future session in view mode.
  isCurrentSession(): boolean {
    return sessionStorage.getItem(this.SESSION_IS_CURRENT_KEY) === '1';
  }

  setIsCurrentSession(isCurrent: boolean): void {
    sessionStorage.setItem(this.SESSION_IS_CURRENT_KEY, isCurrent ? '1' : '0');
  }

  clearIsCurrentSession(): void {
    sessionStorage.removeItem(this.SESSION_IS_CURRENT_KEY);
  }
}
